import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import {
  AlertTriangle,
  CreditCard,
  Mail,
  LogOut,
  RefreshCw,
  Package,
} from "lucide-react";
import ChangeOrgProfile from "../reusables/ChangeOrgProfile";
import CheckoutPlanPicker from "../reusables/CheckoutPlanPicker";
import axios from "axios";
import {
  fetchAndSetOrganization,
  recheckSubscriptionAndRedirectIfActive,
} from "../../utils/organizationService";

/** Stripe statuses where the sub still exists but payment failed — fix via portal. */
const PAYMENT_ISSUE_STATUSES = new Set(["past_due", "unpaid"]);

function readOrgBillingIds() {
  try {
    const org = JSON.parse(localStorage.getItem("organization"));
    return {
      orgId: org?.orgId?.S ?? null,
      customerId: org?.subscription_details?.M?.stripe_cust_id?.S ?? null,
    };
  } catch {
    return { orgId: null, customerId: null };
  }
}

function viewFromStatus(resolved, subscriptionStatus) {
  if (!resolved) return "unknown";
  if (PAYMENT_ISSUE_STATUSES.has(subscriptionStatus)) return "payment_issue";
  return "ended";
}

export default function SubscriptionEnded() {
  const [err_msg, setErrorMsg] = useState(null);
  const [loader, setLoader] = useState(false);
  /** @type {["loading"|"payment_issue"|"ended"|"unknown", Function]} */
  const [view, setView] = useState("loading");
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  const [portalLoader, setPortalLoader] = useState(false);
  const [retryLoader, setRetryLoader] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "pending") {
      setErrorMsg(
        "Your payment may still be processing. Refresh this page in a moment, or choose a plan below."
      );
      window.history.replaceState({}, "", "/subscription-ended");
    }
  }, []);

  const resolveStatus = useCallback(async () => {
    const { orgId, customerId } = readOrgBillingIds();
    // No Stripe customer ever existed — nothing to look up; the picker is correct.
    if (!orgId || !customerId) return "ended";

    const { redirected, subscriptionStatus, resolved } =
      await recheckSubscriptionAndRedirectIfActive();
    if (redirected) return "loading";
    return viewFromStatus(resolved, subscriptionStatus);
  }, []);

  // Resolve live Stripe status and unlock if already active/trialing
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = await resolveStatus();
      if (!cancelled) setView(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [resolveStatus]);

  const handleRetryStatus = async () => {
    setErrorMsg(null);
    setRetryLoader(true);
    setShowPlanPicker(false);
    try {
      const next = await resolveStatus();
      setView(next);
    } finally {
      setRetryLoader(false);
    }
  };

  const openManageSubscription = async () => {
    setErrorMsg(null);
    setPortalLoader(true);
    try {
      const { orgId, customerId } = readOrgBillingIds();
      if (!customerId || !orgId) {
        setErrorMsg("Unable to open billing portal. Please contact support.");
        setPortalLoader(false);
        return;
      }
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "stripe/manage-billing",
        {
          customer_id: customerId,
          organization_id: orgId,
        }
      );
      if (response?.data?.url) {
        window.location.href = response.data.url;
      } else {
        setErrorMsg("Unable to open billing portal. Please contact support.");
      }
    } catch (error) {
      console.error(error);
      setErrorMsg(
        error.response?.data?.message ||
          "Unable to open billing portal. Please contact support."
      );
    } finally {
      setPortalLoader(false);
    }
  };

  const changeOrganization = async (org_id) => {
    try {
      await fetchAndSetOrganization(org_id);
      window.open("/dashboard", "_self");
    } catch (error) {
      console.error(
        "SubscriptionEnded: change organization failed",
        error.response?.data || error.message || error
      );
      if (error.response && error.response.data) {
        setErrorMsg(error.response.data.message);
      }
      setLoader(false);
    }
  };

  const showingPicker = view === "ended" || (view === "unknown" && showPlanPicker);

  const title =
    view === "payment_issue"
      ? "Payment Failed"
      : view === "unknown"
        ? "Couldn't Load Subscription"
        : "Subscription Ended";

  const subtitle =
    view === "payment_issue"
      ? "We couldn't charge your payment method. Update your billing details to restore access."
      : view === "unknown"
        ? "We couldn't load your subscription status. Retry, or manage billing if you already have a plan."
        : "Your subscription has ended. Choose a plan to continue using Twilanswer.";

  const planPicker = <CheckoutPlanPicker onError={setErrorMsg} />;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F8FAFC",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
      }}
    >
      <Card
        sx={{
          width: 420,
          p: 4,
          borderRadius: "14px",
          textAlign: "center",
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            mx: "auto",
            mb: 2,
            borderRadius: "50%",
            bgcolor: "#FEE2E2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AlertTriangle size={24} color="#DC2626" />
        </Box>

        <Typography variant="h6" fontWeight={600} mb={0.5}>
          {title}
        </Typography>

        <Typography fontSize={14} color="text.secondary" mb={3}>
          {subtitle}
        </Typography>

        {err_msg && (
          <Typography fontSize={13} color="error" sx={{ mb: 2 }}>
            {err_msg}
          </Typography>
        )}

        {view === "loading" && (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {view === "payment_issue" && (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={
              portalLoader ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : (
                <CreditCard size={16} />
              )
            }
            disabled={portalLoader}
            sx={{
              py: 1.2,
              borderRadius: "8px",
              mb: 1.5,
              textTransform: "none",
              fontWeight: 600,
            }}
            onClick={openManageSubscription}
          >
            {portalLoader ? "Opening…" : "Manage Billing"}
          </Button>
        )}

        {view === "ended" && (
          <>
            {planPicker}
            <Button
              fullWidth
              variant="text"
              disabled={portalLoader}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: 13,
                color: "#6B7280",
                mb: 1,
              }}
              onClick={openManageSubscription}
            >
              {portalLoader ? "Opening…" : "Manage billing"}
            </Button>
          </>
        )}

        {view === "unknown" && !showPlanPicker && (
          <>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={
                retryLoader ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : (
                  <RefreshCw size={16} />
                )
              }
              disabled={retryLoader}
              sx={{
                py: 1.2,
                borderRadius: "8px",
                mb: 1.5,
                textTransform: "none",
                fontWeight: 600,
              }}
              onClick={handleRetryStatus}
            >
              {retryLoader ? "Retrying…" : "Retry"}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={
                portalLoader ? (
                  <CircularProgress size={16} />
                ) : (
                  <CreditCard size={16} />
                )
              }
              disabled={portalLoader}
              sx={{
                py: 1.2,
                borderRadius: "8px",
                mb: 1.5,
                textTransform: "none",
                fontWeight: 600,
              }}
              onClick={openManageSubscription}
            >
              {portalLoader ? "Opening…" : "Manage Billing"}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<Package size={16} />}
              sx={{
                py: 1.2,
                borderRadius: "8px",
                mb: 1,
                textTransform: "none",
                fontWeight: 600,
              }}
              onClick={() => setShowPlanPicker(true)}
            >
              Subscribe to New Plan
            </Button>
          </>
        )}

        {view === "unknown" && showPlanPicker && (
          <>
            {planPicker}
            <Button
              fullWidth
              variant="text"
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: 13,
                color: "#6B7280",
                mb: 1,
              }}
              onClick={() => setShowPlanPicker(false)}
            >
              Back
            </Button>
          </>
        )}

        {view !== "loading" && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Mail size={16} />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              py: 1.2,
              borderRadius: "8px",
              mb: 3,
            }}
            onClick={() => {
              window.location.href = "mailto:hello@twilanswer.com";
            }}
          >
            Contact Support
          </Button>
        )}

        {showingPicker && (
          <Box textAlign="left" mb={3}>
            <Typography fontSize={13} fontWeight={600} mb={1}>
              What you'll get with a paid plan:
            </Typography>

            {[
              "Call Routing and Automation",
              "Outbound Dialing",
              "Notifications Across Channels",
            ].map((item) => (
              <Box
                key={item}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "#22C55E",
                  }}
                />
                <Typography fontSize={13} color="text.secondary">
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <div className="px-3 w-full mb-5">
          <ChangeOrgProfile changeOrganization={changeOrganization} />
        </div>

        <Button
          startIcon={<LogOut size={16} />}
          sx={{
            textTransform: "none",
            color: "#6B7280",
            fontWeight: 500,
            border: "1px solid #D1D5DB",
            width: "100%",
          }}
          onClick={() => {
            console.info(
              "[Auth] Logging out: user chose Sign out from subscription-ended page."
            );
            localStorage.clear();
            window.location.href = "/login";
          }}
        >
          Sign out
        </Button>
      </Card>
    </Box>
  );
}
